import { useState } from 'react';
import { Tag, TextField, Checkbox, TabGroup, Tab } from '@humanforce/mango-ui-react';
import { SectionCard } from '../components/SectionCard';
import { PayGroupsPanel } from '../components/PayGroupsPanel';
import type { OrgData, OrgBusinessRecord } from '../organisationFixtures';

interface BusinessRecordsTabProps {
  orgId: string;
  orgData: OrgData;
}

interface LegalDraft {
  number: string;
  branch: string;
  legalName: string;
  tradingName: string;
  taxSignatory: string;
  eft: string;
  withholding: string;
  regionalTax: boolean;
}

interface StpDraft {
  bmsId: string;
  reportingParty: string;
  branch2: string;
  ssid: string;
  phase: string;
  lastSubmission: string;
}

const toneToVariant = (tone: 'brand' | 'caution' | 'positive'): 'positive' | 'caution' | 'neutral' => {
  if (tone === 'positive') return 'positive';
  if (tone === 'caution') return 'caution';
  return 'neutral';
};

const DisplayField = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="mgo-text-xs mgo-weight-strong mgo-color-muted" style={{ textTransform: 'uppercase' }}>{label}</div>
    <div className="mgo-text-s" style={{ marginTop: 'var(--mgo-space-05)' }}>{value}</div>
  </div>
);

const legalDraftFrom = (rec: OrgBusinessRecord): LegalDraft => ({
  number: rec.legal.number,
  branch: rec.legal.branch,
  legalName: rec.legal.legalName,
  tradingName: rec.legal.tradingName,
  taxSignatory: rec.legal.taxSignatory,
  eft: rec.legal.eft,
  withholding: rec.legal.withholding,
  regionalTax: rec.legal.regionalTax,
});

const stpDraftFrom = (rec: OrgBusinessRecord): StpDraft => ({
  bmsId: rec.stp.bmsId,
  reportingParty: rec.stp.reportingParty,
  branch2: rec.stp.branch2,
  ssid: rec.stp.ssid,
  phase: rec.stp.phase,
  lastSubmission: rec.stp.lastSubmission,
});

export const BusinessRecordsTab = ({ orgId: _orgId, orgData }: BusinessRecordsTabProps) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [businessRecords, setBusinessRecords] = useState(() => orgData.businessRecords.map((rec) => ({ ...rec, legal: { ...rec.legal }, stp: { ...rec.stp }, payrolls: [...rec.payrolls] })));
  const [legalEditing, setLegalEditing] = useState(false);
  const [stpEditing, setStpEditing] = useState(false);

  const [legalDraft, setLegalDraft] = useState<LegalDraft>(() => businessRecords.length > 0 ? legalDraftFrom(businessRecords[0]) : { number: '', branch: '', legalName: '', tradingName: '', taxSignatory: '', eft: '', withholding: '', regionalTax: false });
  const [stpDraft, setStpDraft] = useState<StpDraft>(() => businessRecords.length > 0 ? stpDraftFrom(businessRecords[0]) : { bmsId: '', reportingParty: '', branch2: '', ssid: '', phase: '', lastSubmission: '' });

  if (businessRecords.length === 0)
    return <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>No business records found.</p>;

  const selectRecord = (index: number) => {
    setSelectedIndex(index);
    setLegalEditing(false);
    setStpEditing(false);
    setLegalDraft(legalDraftFrom(businessRecords[index]));
    setStpDraft(stpDraftFrom(businessRecords[index]));
  };

  const saveLegal = () => {
    setBusinessRecords((records) => records.map((record, index) => index === selectedIndex ? { ...record, legal: { ...record.legal, ...legalDraft } } : record));
    setLegalEditing(false);
  };

  const saveStp = () => {
    setBusinessRecords((records) => records.map((record, index) => index === selectedIndex ? { ...record, stp: { ...record.stp, ...stpDraft } } : record));
    setStpEditing(false);
  };

  const legalFieldStyle = legalEditing ? {} : { pointerEvents: 'none' as const };
  const stpFieldStyle = stpEditing ? {} : { pointerEvents: 'none' as const };

  return (
    <TabGroup
      value={businessRecords[selectedIndex].name}
      onValueChange={(name) => {
        const index = businessRecords.findIndex((r) => r.name === name);
        if (index < 0) return;
        selectRecord(index);
      }}
    >
      {businessRecords.map((rec, recIndex) => (
        <Tab key={rec.name} value={rec.name} label={rec.name}>
          <div className="mgo-stack mgo-gap-l">
            <div style={{ alignItems: 'start', display: 'grid', gap: 'var(--mgo-space-40)', gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)' }}>
              <SectionCard
                icon="archive"
                isEditing={legalEditing}
                title={orgData.labels.number}
                onCancel={() => { setLegalDraft(legalDraftFrom(rec)); setLegalEditing(false); }}
                onEdit={() => setLegalEditing(true)}
                onSave={saveLegal}
              >
                {legalEditing
                  ? (
                    <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s" style={legalFieldStyle}>
                      <TextField label={orgData.labels.number} value={legalDraft.number} onChange={(e) => setLegalDraft((d) => ({ ...d, number: e.target.value }))} />
                      <TextField label="Branch" value={legalDraft.branch} onChange={(e) => setLegalDraft((d) => ({ ...d, branch: e.target.value }))} />
                      <TextField label="Legal Name" value={legalDraft.legalName} onChange={(e) => setLegalDraft((d) => ({ ...d, legalName: e.target.value }))} />
                      <TextField label="Trading Name" value={legalDraft.tradingName} onChange={(e) => setLegalDraft((d) => ({ ...d, tradingName: e.target.value }))} />
                      <TextField label="Tax Signatory" value={legalDraft.taxSignatory} onChange={(e) => setLegalDraft((d) => ({ ...d, taxSignatory: e.target.value }))} />
                      <TextField label={orgData.labels.eft} value={legalDraft.eft} onChange={(e) => setLegalDraft((d) => ({ ...d, eft: e.target.value }))} />
                      <TextField label={orgData.labels.withholding} value={legalDraft.withholding} onChange={(e) => setLegalDraft((d) => ({ ...d, withholding: e.target.value }))} />
                      <Checkbox id={`regional-tax-${recIndex}`} label="Regional Tax" checked={legalDraft.regionalTax} onChange={(checked) => setLegalDraft((d) => ({ ...d, regionalTax: checked }))} />
                    </div>
                  )
                  : (
                    <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s">
                      <DisplayField label={orgData.labels.number} value={rec.legal.number} />
                      <DisplayField label="Branch" value={rec.legal.branch} />
                      <DisplayField label="Legal Name" value={rec.legal.legalName} />
                      <DisplayField label="Trading Name" value={rec.legal.tradingName} />
                      <DisplayField label="Tax Signatory" value={rec.legal.taxSignatory} />
                      <DisplayField label={orgData.labels.eft} value={rec.legal.eft} />
                      <DisplayField label={orgData.labels.withholding} value={rec.legal.withholding} />
                      <DisplayField label="Regional Tax" value={rec.legal.regionalTax ? 'Yes' : 'No'} />
                    </div>
                  )}
              </SectionCard>

              <SectionCard
                icon="task-list"
                isEditing={stpEditing}
                title={orgData.labels.reportingTitle}
                onCancel={() => { setStpDraft(stpDraftFrom(rec)); setStpEditing(false); }}
                onEdit={() => setStpEditing(true)}
                onSave={saveStp}
              >
                {stpEditing
                  ? (
                    <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s" style={stpFieldStyle}>
                      <TextField label={orgData.labels.bmsId} value={stpDraft.bmsId} onChange={(e) => setStpDraft((d) => ({ ...d, bmsId: e.target.value }))} />
                      <label className="mgo-stack mgo-gap-xs mgo-text-s mgo-weight-strong mgo-color-neutral">
                        Reporting Party
                        <select
                          className="select-control"
                          value={stpDraft.reportingParty}
                          onChange={(e) => setStpDraft((d) => ({ ...d, reportingParty: e.target.value }))}
                        >
                          <option value="Self-lodging">Self-lodging</option>
                          <option value="Registered agent">Registered agent</option>
                          <option value="Intermediary">Intermediary</option>
                        </select>
                      </label>
                      <TextField label={orgData.labels.branch2} value={stpDraft.branch2} onChange={(e) => setStpDraft((d) => ({ ...d, branch2: e.target.value }))} />
                      <TextField label={orgData.labels.ssid} value={stpDraft.ssid} onChange={(e) => setStpDraft((d) => ({ ...d, ssid: e.target.value }))} />
                      <TextField label="Phase" value={stpDraft.phase} onChange={(e) => setStpDraft((d) => ({ ...d, phase: e.target.value }))} />
                      <TextField label="Last Submission" value={stpDraft.lastSubmission} onChange={(e) => setStpDraft((d) => ({ ...d, lastSubmission: e.target.value }))} />
                      <Tag tone={toneToVariant(rec.stp.tone)} label={rec.stp.lastSubmission} />
                    </div>
                  )
                  : (
                    <div className="mgo-grid mgo-grid-cols-2 mgo-gap-s">
                      <DisplayField label={orgData.labels.bmsId} value={rec.stp.bmsId} />
                      <DisplayField label="Reporting Party" value={rec.stp.reportingParty} />
                      <DisplayField label={orgData.labels.branch2} value={rec.stp.branch2} />
                      <DisplayField label={orgData.labels.ssid} value={rec.stp.ssid} />
                      <DisplayField label="Phase" value={rec.stp.phase} />
                      <DisplayField label="Last Submission" value={rec.stp.lastSubmission} />
                      <Tag tone={toneToVariant(rec.stp.tone)} label={rec.stp.lastSubmission} />
                    </div>
                  )}
              </SectionCard>
            </div>

            <SectionCard
              icon="payroll-dollar"
              title={orgData.labels.payrollTitle}
            >
              <PayGroupsPanel businessRecords={[rec]} />
            </SectionCard>
          </div>
        </Tab>
      ))
      }
    </TabGroup >
  );
};
